import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem, springs } from '../../lib/animations';
import { countryFlag } from '../../lib/country-utils';
import AnimatedButton from '../AnimatedButton';
import NamePromptModal from '../NamePromptModal';

/* ── Types ── */
interface VirtualJoint {
  id: number;
  creator_name: string;
  country?: string;
  strain_name: string;
  strain_type?: string;
  mix_type: string;
  mix_ratio: number;
  paper_type: string;
  filter_type: string;
  roll_score: number;
  aesthetics_score?: number;
  strain_quality_score?: number;
  overall_score: number;
  avg_rating?: number;
  total_ratings?: number;
  times_passed?: number;
  created_at?: string;
}

type SortOption = 'recent' | 'top_rated' | 'most_passed';

/* ── Helpers ── */
function typeBadgeColor(type?: string | null | any) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'sativa': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'indica': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'hybrid': return 'bg-green-500/20 text-green-300 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

function mixLabel(key: string): string {
  switch (key) {
    case 'pure': return 'Pure';
    case 'tobacco': return 'Tobacco Spliff';
    case 'herbal': return 'Herbal Mix';
    case 'moonrock': return 'Moon Rock';
    default: return key;
  }
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

function relativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function letterGrade(score: number): string {
  if (score >= 97) return 'A+';
  if (score >= 93) return 'A';
  if (score >= 90) return 'A-';
  if (score >= 87) return 'B+';
  if (score >= 83) return 'B';
  if (score >= 80) return 'B-';
  if (score >= 77) return 'C+';
  if (score >= 73) return 'C';
  if (score >= 70) return 'C-';
  if (score >= 60) return 'D';
  return 'F';
}

function gradeColor(grade: string): string {
  if (grade.startsWith('A')) return 'text-[#4ade80]';
  if (grade.startsWith('B')) return 'text-blue-400';
  if (grade.startsWith('C')) return 'text-yellow-400';
  if (grade.startsWith('D')) return 'text-orange-400';
  return 'text-red-400';
}

/* ── Sort Tabs ── */
const SORT_OPTIONS: { key: SortOption; label: string }[] = [
  { key: 'recent', label: 'Recent' },
  { key: 'top_rated', label: 'Top Rated' },
  { key: 'most_passed', label: 'Most Passed' },
];

/* ── Joint Card ── */
function JointCard({
  joint,
  onRate,
}: {
  joint: VirtualJoint;
  onRate: (id: number, rating: number, comment: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rating, setRating] = useState(7);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const grade = letterGrade(joint.overall_score);
  const avgDisplay = joint.avg_rating != null ? joint.avg_rating.toFixed(1) : '--';

  const handleSubmitRating = async () => {
    setSubmitting(true);
    try {
      await onRate(joint.id, rating, comment);
      setSubmitted(true);
      setExpanded(false);
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      variants={staggerItem}
      className="bg-cannabis-800/50 border border-white/5 rounded-2xl overflow-hidden"
    >
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-cream font-semibold text-sm">{joint.creator_name}</span>
            {joint.country && (
              <span className="text-sm" title={joint.country}>{countryFlag(joint.country)}</span>
            )}
          </div>
          {joint.created_at && (
            <span className="text-cream/30 text-[10px]">{relativeTime(joint.created_at)}</span>
          )}
        </div>

        {/* Strain info */}
        <div className="flex items-center gap-2">
          <span className="text-cream/80 text-sm font-medium">{joint.strain_name}</span>
          {joint.strain_type && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadgeColor(joint.strain_type)}`}>
              {joint.strain_type}
            </span>
          )}
        </div>

        {/* Build details */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4a574]/10 text-[#d4a574] border border-[#d4a574]/20">
            {mixLabel(joint.mix_type)} {joint.mix_ratio}%
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-cream/50 border border-white/10">
            {joint.paper_type}
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-cream/50 border border-white/10">
            {joint.filter_type}
          </span>
        </div>

        {/* Scores row */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-cream/40 text-[10px]">Roll</span>
            <span className="text-cream text-xs font-semibold">{joint.roll_score}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-cream/40 text-[10px]">Overall</span>
            <span className={`text-xs font-bold ${gradeColor(grade)}`}>
              {joint.overall_score} ({grade})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-cream/40 text-[10px]">Rating</span>
            <span className="text-[#4ade80] text-xs font-semibold">
              {avgDisplay}/10
            </span>
            {joint.total_ratings != null && joint.total_ratings > 0 && (
              <span className="text-cream/30 text-[10px]">({joint.total_ratings})</span>
            )}
          </div>
          {joint.times_passed != null && joint.times_passed > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-cream/40 text-[10px]">Passed</span>
              <span className="text-cream/60 text-xs">{joint.times_passed}x</span>
            </div>
          )}
        </div>

        {/* Rate button */}
        {!submitted && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[#4ade80]/70 text-xs font-semibold hover:text-[#4ade80] transition-colors"
          >
            {expanded ? 'Cancel' : 'Rate This Joint'}
          </button>
        )}
        {submitted && (
          <span className="text-[#4ade80]/50 text-xs">Rated!</span>
        )}
      </div>

      {/* Rating expansion */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 border-t border-white/5 space-y-3">
              <div>
                <label className="text-cream/50 text-xs block mb-2">
                  Rating: <span className="text-cream font-semibold">{rating}/10</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={rating}
                  onChange={e => setRating(Number(e.target.value))}
                  className="w-full accent-[#4ade80]"
                />
                <div className="flex justify-between text-[10px] text-cream/30 mt-0.5">
                  <span>1</span>
                  <span>10</span>
                </div>
              </div>
              <input
                type="text"
                placeholder="Optional comment..."
                value={comment}
                onChange={e => setComment(e.target.value)}
                className="w-full bg-cannabis-900 border border-cannabis-600 rounded-xl px-3 py-2 text-cream text-sm placeholder:text-cream/30 focus:outline-none focus:border-[#4ade80]/50"
              />
              <AnimatedButton
                onClick={handleSubmitRating}
                disabled={submitting}
                loading={submitting}
                size="sm"
              >
                Submit Rating
              </AnimatedButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Main Component ── */
export default function GlobalPassTab() {
  const [joints, setJoints] = useState<VirtualJoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortOption>('recent');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  const fetchJoints = useCallback(async () => {
    setLoading(true);
    try {
      const sortParam = sort === 'top_rated' ? 'popular' : sort === 'most_passed' ? 'passed' : 'recent';
      const res = await fetch(`/api/virtual-joints?sort=${sortParam}&per_page=20`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const items: VirtualJoint[] = data.items ?? data.joints ?? data.data ?? (Array.isArray(data) ? data : []);
      setJoints(items);
    } catch {
      setJoints([]);
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => {
    fetchJoints();
  }, [fetchJoints]);

  const handleRate = useCallback(async (id: number, rating: number, comment: string) => {
    const name = localStorage.getItem('rollerName');
    if (!name) {
      setShowNamePrompt(true);
      throw new Error('Name required');
    }
    const res = await fetch(`/api/virtual-joints/${id}/rate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rater_name: name,
        rating,
        comment: comment || undefined,
      }),
    });
    if (!res.ok) throw new Error('Failed to rate');
    // Refresh to show updated averages
    await fetchJoints();
  }, [fetchJoints]);

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-cream">Global Pass</h3>
        <motion.button
          whileTap={{ scale: 0.95, rotate: 180 }}
          transition={springs.bouncy}
          onClick={fetchJoints}
          disabled={loading}
          className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-cream/50 hover:text-cream hover:border-white/20 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </motion.button>
      </div>

      {/* Sort tabs */}
      <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1 border border-white/5">
        {SORT_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setSort(opt.key)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
              sort === opt.key
                ? 'bg-[#4ade80]/15 text-[#4ade80] border border-[#4ade80]/20'
                : 'text-cream/40 hover:text-cream/60 border border-transparent'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : joints.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-cream/40 text-sm">No joints have been rolled yet. Be the first!</p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-3"
        >
          {joints.map(joint => (
            <JointCard
              key={joint.id}
              joint={joint}
              onRate={handleRate}
            />
          ))}
        </motion.div>
      )}

      <NamePromptModal
        open={showNamePrompt}
        onClose={() => setShowNamePrompt(false)}
        onSubmit={() => setShowNamePrompt(false)}
      />
    </div>
  );
}
